import React, { useMemo, useCallback } from 'react';
import { FormInput, FormSelect, FormTextArea } from '@ihsm/ui-shared';
import { ReportColumnItem } from 'shared/types';
import { ColumnsSelectionArea } from './ColumnsSelectionArea';
import { GroupSelectionArea } from './GroupSelectionArea';
import { REPORT_TYPE_OPTIONS } from '../constants';
import { FormColumnItem, ReportForm } from '../types';
import './NewReportForm.scss';
import { defaultUnitsMock } from '../mock/NewReportMocks';

interface NewReportFormProps {
  form: ReportForm;
  setForm?: (form: ReportForm) => void;
  isDisabled?: boolean;
}

export const NewReportForm: React.FC<NewReportFormProps> = ({
  form,
  setForm = () => {},
  isDisabled = false,
}) => {
  const groupsItems: FormColumnItem[] = useMemo(() => {
    if (!form.groups) return [];
    const items: FormColumnItem[] = form.groups;
    return items;
  }, [form.groups]);

  const onGroupDataChange = useCallback(
    (groups: FormColumnItem[]) => {
      setForm({ ...form, groups });
    },
    [form, setForm]
  );

  const columnsItems: FormColumnItem[] = useMemo(() => {
    if (!form.columns) return [];

    const items: FormColumnItem[] = form.columns.map((column) => ({
      name: column.name,
      category: column.category,
      unit: column.unit,
    }));

    return items;
  }, [form.columns]);

  const onColumnDataChange = useCallback(
    (list: FormColumnItem[]) => {
      const columns: ReportColumnItem[] = list.map((col) => ({
        category: col.category,
        name: col.name,
        unit: col?.unit,
      }));

      setForm({ ...form, columns });
    },
    [form, setForm]
  );

  return (
    <form className="new-report-form">
      <FormInput
        label="Name"
        placeholder="Enter a Report Name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        disabled={isDisabled}
        validation={form.validation?.name}
      />
      <FormTextArea
        label="Description"
        placeholder="Enter a Description (optional)"
        maxLength={200}
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        disabled={isDisabled}
        validation={form.validation?.description}
      />
      <FormSelect
        label="Report Type"
        options={REPORT_TYPE_OPTIONS}
        value={form.type}
        onChange={(e) => setForm({ ...form, type: e.target.value })}
        disabled={isDisabled}
        validation={form.validation?.type}
      />
      {form.type === 'Custom' ? (
        <>
          <FormSelect
            label="Default Unit"
            options={defaultUnitsMock}
            value={form.defaultUnit}
            onChange={(e) => setForm({ ...form, defaultUnit: e.target.value })}
            disabled={isDisabled}
            validation={form.validation?.defaultUnit}
          />

          <h3 className="new-report-form__title">Report Designer</h3>
          <div className="new-report-form__group-content">
            <GroupSelectionArea
              currentData={groupsItems}
              onDataChange={onGroupDataChange}
              isDisabled={isDisabled}
              validation={form.validation?.groups}
            />
          </div>
          <div className="new-report-form__columns-content">
            <ColumnsSelectionArea
              data={columnsItems}
              onDataChange={onColumnDataChange}
              isDisabled={isDisabled}
              validation={form.validation?.columns}
            />
          </div>
        </>
      ) : null}
    </form>
  );
};
